import { useCallback, useRef, useState } from 'react'

import { MeetingsMeetingProviderUpdatePageContentDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'

import { type PageType } from '../../MeetingsMeetingContext'

interface UseAutosaveReturn {
  isSavingNotes: boolean
  setNotes: (json: string) => void
  getCleanup: () => () => void
}

interface UseAutosaveProps {
  page: PageType | null
  onError: (error: boolean, message: string | null) => void
}

export const useAutosave = ({ page, onError }: UseAutosaveProps): UseAutosaveReturn => {
  const autosaveTimeoutRef = useRef<NodeJS.Timeout>()
  const [isSavingNotes, setIsSavingNotes] = useState(false)

  // Mutation for updating notes
  const [updateNotes] = useMutation(MeetingsMeetingProviderUpdatePageContentDocument, {
    extractorFn: (data) => data.pagesMutations.updatePagesPage,
  })

  const handleAutosave = useCallback(
    async (notesContent: string) => {
      try {
        if (!page?.id) throw new Error('Page ID is required')
        setIsSavingNotes(true)
        await updateNotes({
          variables: {
            content: notesContent,
            id: page.id,
          },
        })
      } catch (err) {
        onError(true, err instanceof Error ? err.message : 'Failed to save notes')
      } finally {
        setIsSavingNotes(false)
      }
    },
    [updateNotes, page?.id, onError]
  )

  const debouncedAutosave = useCallback(
    (notesContent: string) => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current)
      }
      autosaveTimeoutRef.current = setTimeout(() => {
        handleAutosave(notesContent)
      }, 1000) // Wait 1 second after the last change
    },
    [handleAutosave]
  )

  const setNotes = useCallback(
    (json: string) => {
      try {
        JSON.parse(json)
        debouncedAutosave(json)
      } catch (e) {
        // Invalid JSON, don't save
      }
    },
    [debouncedAutosave]
  )

  // Cleanup timeout on unmount
  const cleanup = useCallback(() => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current)
    }
  }, [])

  // Return cleanup function to be called by the component
  const getCleanup = useCallback(() => cleanup, [cleanup])

  return {
    isSavingNotes,
    setNotes,
    getCleanup,
  }
}
